import { RotateCcw } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { CSVExportButton } from "@/components/csv-export-button";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import { DrilldownChartControls } from "@/components/metrics/drilldown-chart-controls";
import { DrilldownChartErrorBoundary } from "@/components/metrics/drilldown-chart-error-boundary";
import { DrilldownLimitAlert } from "@/components/metrics/drilldown-limit-alert";
import { DrilldownTimeSeriesChart } from "@/components/metrics/drilldown-timeseries-chart";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { CategoryStyle } from "@/components/metrics/types";
import { SortableTable } from "@/components/sortable-table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTableCSVExport } from "@/hooks/use-csv-export";
import { useDrilldownChartState } from "@/hooks/use-drilldown-chart-state";
import { formatDate, formatDateForFilename } from "@/lib/date-formatting";
import { isNumber } from "@/lib/is-number";
import type { UnifiedTableColumn } from "@/types/table-column";

// Generic type for drilldown items
type DrilldownItem = {
  [key: string]: string | number | null | undefined;
};

// Generic metric config interface
export interface MetricConfig<TMetrics = unknown> {
  title: string;
  tooltip: string;
  category: string;
  drilldownType: "table" | "chart";
  primaryKey: string[];
  drilldownColumns?: UnifiedTableColumn<unknown>[];
  defaultSort?: { key: string; direction: "asc" | "desc" };
  // Optional secondary metric relationship (e.g., accounts_engaged -> engagements_per_account)
  secondaryMetricKey?: string;
  // Optional function to generate the label for the secondary value
  getSecondaryValueLabel?: (secondaryValue: number | undefined) => string;
  render?: (
    value: number | null,
    options: { metricKey: string; metrics?: TMetrics },
  ) => React.ReactNode;
}

// Props for the drilldown hook result
export interface DrilldownHookResult {
  data: unknown[] | undefined;
  isLoading: boolean;
}

// Props for the generic MetricDrilldown component
export interface MetricDrilldownProps<TMetrics = unknown> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metric: string;
  metricConfig: MetricConfig<TMetrics>;
  metrics?: TMetrics;
  drilldownData: unknown[] | undefined;
  isLoadingDrilldown: boolean;
  loadingStates?: Record<string, { isLoading: boolean; isError: boolean }>;
  drillDownLimit?: number;
  entityName: string; // For CSV filename (e.g., "Paid Social", "Email")
  tableName: string; // For table state persistence (e.g., "channels::drilldown::accounts_engaged")
}

// Compact Metric Card Component
function CompactMetricCard<
  TMetrics extends Record<string, unknown> = Record<string, unknown>,
>({
  metric,
  metricConfig,
  metrics,
  loadingStates,
}: {
  metric: string;
  metricConfig: MetricConfig<TMetrics>;
  metrics?: TMetrics;
  loadingStates?: Record<string, { isLoading: boolean; isError: boolean }>;
}) {
  const categoryStyle = CATEGORY_STYLES[
    metricConfig.category as keyof typeof CATEGORY_STYLES
  ] as CategoryStyle;

  // Type-safe access to metrics - cast once at the top
  const typedMetrics = metrics as Record<string, unknown> | undefined;
  const metricValue = typedMetrics?.[metric];

  // Handle 0 properly - 0 is a valid numeric value, not null
  const rawValue = typeof metricValue === "number" ? metricValue : null;
  const isLoading = loadingStates?.[metric]?.isLoading || false;

  // Check if this metric would have secondary content
  const hasSecondaryContent = (() => {
    // Special case: For accounts_engaged, people_engaged, if value is 0, treat as simple metric
    if (
      (metric === "accounts_engaged" || metric === "people_engaged") &&
      rawValue === 0
    ) {
      return false;
    }

    // Amount metrics themselves should be treated as simple metrics in drilldown cards
    // They don't have secondary content - they ARE the secondary content for count metrics
    if (metric.includes("amount")) {
      return false;
    }

    // Check if the metric config defines a secondary metric
    // BUT: If the secondary metric is an amount, don't show it in the drilldown card
    // Count metrics with amount secondaries should display as simple metrics in drilldown
    if (metricConfig.secondaryMetricKey) {
      const isSecondaryAnAmount =
        metricConfig.secondaryMetricKey.includes("amount");
      if (isSecondaryAnAmount) {
        return false;
      }
      return true;
    }

    // Metrics with per-entity secondary values (legacy check for metrics without config)
    if (metric.includes("per_")) {
      return true;
    }

    return false;
  })();

  // Check if we have the primary value but full metrics are still loading
  // This happens when the user clicks a metric and we show the provided value immediately
  // For metrics with specific secondary metrics, verify that secondary metric exists
  const secondaryMetricKey = metricConfig.secondaryMetricKey;
  const hasMetricsData = (() => {
    if (!metrics || Object.keys(metrics).length <= 1) return false;
    // If we need a specific secondary metric, verify it exists
    if (secondaryMetricKey && hasSecondaryContent) {
      return secondaryMetricKey in metrics;
    }
    // Otherwise, just check if we have multiple metrics
    return true;
  })();
  const showSecondaryLoading =
    hasSecondaryContent && rawValue !== null && !hasMetricsData && !isLoading;

  // For simple metrics, render just the formatted primary value
  let displayValue: React.ReactNode;

  if (!hasSecondaryContent) {
    if (rawValue === null || rawValue === undefined) {
      displayValue = "—";
    } else {
      const isCurrency = metric.includes("amount");
      if (isCurrency) {
        displayValue = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(rawValue);
      } else if (metric.includes("date")) {
        displayValue = rawValue ? formatDate(String(rawValue)) : "—";
      } else {
        displayValue = new Intl.NumberFormat("en-US").format(rawValue);
      }
    }
  } else if (showSecondaryLoading) {
    // Show primary value immediately, secondary will show loading skeleton
    const isCurrency = metric.includes("amount");
    if (isCurrency) {
      displayValue = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(rawValue!);
    } else {
      displayValue = new Intl.NumberFormat("en-US").format(rawValue!);
    }
  } else {
    // For compact metric cards, format values directly with appropriate font sizes
    // rather than using the full render function which has large responsive fonts
    const isCurrency = metric.includes("amount");
    const primaryFormatted =
      rawValue === null || rawValue === undefined
        ? "—"
        : isCurrency
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(rawValue)
          : new Intl.NumberFormat("en-US").format(rawValue);

    // Get secondary value if needed
    const secondaryMetricKey = metricConfig.secondaryMetricKey;
    let secondaryContent: React.ReactNode = null;

    if (secondaryMetricKey && metrics) {
      const secondaryValue = metrics[secondaryMetricKey];
      if (typeof secondaryValue === "number" && rawValue !== 0) {
        const formattedSecondary = metricConfig.secondaryMetricKey?.includes(
          "amount",
        )
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(secondaryValue)
          : secondaryValue.toFixed(1);

        const label =
          metricConfig.getSecondaryValueLabel?.(secondaryValue) || "";
        secondaryContent = (
          <span className="inline-flex items-center gap-1">
            <span>{formattedSecondary}</span>
            {label && (
              <span className="font-normal whitespace-nowrap">{label}</span>
            )}
          </span>
        );
      }
    }

    displayValue = (
      <div className="flex flex-col">
        <div className="text-lg font-semibold">{primaryFormatted}</div>
        <div className="min-h-[20px] text-sm text-muted-foreground/50">
          {secondaryContent}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg border shadow-sm animate-pulse">
        <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
          <div className="w-4 h-4 bg-muted-foreground/20 rounded"></div>
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <div className="h-6 bg-muted-foreground/20 rounded w-16"></div>
          {hasSecondaryContent && (
            <div className="h-4 bg-muted-foreground/20 rounded w-24"></div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-card rounded-lg border shadow-sm">
      <div
        className={`flex items-center justify-center w-8 h-8 rounded ${categoryStyle.color}`}
      >
        {categoryStyle.icon}
      </div>

      <div className="flex-1 min-w-0">
        {showSecondaryLoading ? (
          // Show primary value with loading skeleton for secondary
          <div className="flex flex-col">
            <div className="text-lg font-semibold">{displayValue}</div>
            <div className="min-h-[24px] flex items-center">
              <div className="h-4 bg-muted-foreground/20 rounded w-32 animate-pulse"></div>
            </div>
          </div>
        ) : !hasSecondaryContent ? (
          <div className="flex items-center text-lg font-semibold">
            {displayValue}
          </div>
        ) : (
          displayValue
        )}
      </div>
    </div>
  );
}

export function MetricDrilldown<
  TMetrics extends Record<string, unknown> = Record<string, unknown>,
>({
  open,
  onOpenChange,
  metric,
  metricConfig,
  metrics,
  drilldownData,
  isLoadingDrilldown,
  loadingStates,
  drillDownLimit,
  entityName,
  tableName,
}: MetricDrilldownProps<TMetrics>) {
  const showResetButton = metricConfig.drilldownType !== "chart";
  const [resetTrigger, setResetTrigger] = useState<number | undefined>(
    undefined,
  );

  const {
    viewMode,
    setViewMode,
    cumulative,
    setCumulative,
    dateColumns,
    typedRows: typedDrilldownRows,
    hasDateColumns,
    showChartToggle,
    grouping,
    handleGroupingChange,
  } = useDrilldownChartState({
    metricKey: metric,
    drilldownColumns: metricConfig.drilldownColumns,
    drilldownType: metricConfig.drilldownType,
    rows: drilldownData,
  });

  // Type-safe access to metrics - cast once at the top
  const typedMetrics = metrics as Record<string, unknown> | undefined;
  const metricValue = typedMetrics?.[metric];
  // Skip limit check for amount/currency metrics since they show dollar values, not row counts
  const isAmountMetric = metric.includes("amount");
  const isDrillDownEnabled =
    isNumber(drillDownLimit) && isNumber(metricValue) && !isAmountMetric
      ? metricValue < drillDownLimit
      : true;

  // Setup CSV export
  const { isExporting, exportData, defaultFilename } =
    useTableCSVExport<DrilldownItem>({
      data: drilldownData ? (drilldownData as unknown as DrilldownItem[]) : [],
      columns: metricConfig.drilldownColumns || [],
      filename: `${entityName}-${metricConfig.title}-${formatDateForFilename(
        new Date(),
      )}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Check if we have exportable data
  const hasExportableData = useMemo(() => {
    return (
      metricConfig.drilldownType !== "chart" &&
      drilldownData &&
      Array.isArray(drilldownData) &&
      drilldownData.length > 0
    );
  }, [metricConfig.drilldownType, drilldownData]);

  // Pass null to SortableTable when loading to ensure skeleton rows are shown
  const tableData = isLoadingDrilldown
    ? null
    : ((drilldownData ?? []) as unknown as DrilldownItem[]);

  // Calculate loading row count based on metric value
  // Cap at 20 to avoid excessive skeleton rows for very large values
  const loadingRowCount = useMemo(() => {
    if (typeof metricValue === "number" && metricValue > 0) {
      return Math.min(metricValue, 20);
    }
    return 5; // Default fallback
  }, [metricValue]);

  // Reset handler
  const handleResetTable = () => {
    setResetTrigger((prev) => (prev ?? -1) + 1);
  };

  function renderContent() {
    if (!isDrillDownEnabled && drillDownLimit) {
      return (
        <div className="flex justify-center items-center p-4 py-12">
          <DrilldownLimitAlert className="max-w-md" limit={drillDownLimit} />
        </div>
      );
    }
    if (metricConfig.drilldownType === "chart") {
      return (
        <div className="p-4 py-12 text-center">
          <p className="text-lg text-muted-foreground">
            Chart visualization is not implemented for this metric.
          </p>
        </div>
      );
    }

    // Chart mode for table-type metrics with date columns
    if (viewMode === "chart" && hasDateColumns && drilldownData) {
      return (
        <div className="h-full flex flex-col">
          <DrilldownChartErrorBoundary>
            <DrilldownTimeSeriesChart
              data={typedDrilldownRows}
              columns={dateColumns}
              grouping={grouping}
              cumulative={cumulative}
            />
          </DrilldownChartErrorBoundary>
        </div>
      );
    }

    return (
      <div className="max-h-[calc(80vh-14rem)] overflow-y-auto">
        <SortableTable<DrilldownItem>
          tableName={tableName}
          columns={metricConfig.drilldownColumns || []}
          data={tableData}
          keyExtractor={(item) => {
            const typedItem = item as Record<string, string | number>;
            // Use primaryKey fields to generate a unique identifier
            // If primaryKey is not sufficient, fallback to stringifying the entire item
            const keyParts = (metricConfig.primaryKey || ["id"])
              .map((key) => typedItem[key] ?? "")
              .join("-");
            // If key is empty or just dashes, use a hash of the entire object
            if (!keyParts || keyParts.replace(/-/g, "") === "") {
              return JSON.stringify(item);
            }
            return keyParts;
          }}
          isLoading={isLoadingDrilldown}
          loadingRowCount={loadingRowCount}
          defaultSort={metricConfig.defaultSort}
          resetTrigger={resetTrigger}
          disableInternalScrolling={true}
          headerClassName="-top-px"
        />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby="metric-drilldown-content"
        className="w-[90vw] max-w-full max-h-[90vh] flex flex-col overflow-hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="flex flex-row items-end justify-between pb-2 space-y-0 pr-0">
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-xl font-semibold">
                {metricConfig.title}
              </DialogTitle>
              <CompactMetricCard
                metric={metric}
                metricConfig={metricConfig}
                metrics={metrics}
                loadingStates={loadingStates}
              />
            </div>

            {metricConfig.tooltip && (
              <p className="text-sm text-muted-foreground">
                {metricConfig.tooltip}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-4">
            <DrilldownChartControls
              grouping={grouping}
              onGroupingChange={handleGroupingChange}
              cumulative={cumulative}
              onCumulativeChange={setCumulative}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showChartControls={viewMode === "chart" && hasDateColumns}
              showChartToggle={showChartToggle}
            />

            {showResetButton && viewMode === "table" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleResetTable}
                    className="h-8 w-8 bg-card"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset table to default state</p>
                </TooltipContent>
              </Tooltip>
            )}
            {hasExportableData && (
              <ExportFilenameDialog
                open={isExportDialogOpen}
                onOpenChange={setIsExportDialogOpen}
                defaultFilename={defaultFilename}
                extension="csv"
                onExport={handleExport}
                disabled={isExporting}
              >
                <CSVExportButton
                  onClick={() => setIsExportDialogOpen(true)}
                  isExporting={isExporting}
                  tooltipText="Export as CSV"
                  disabled={isExporting}
                  className="bg-card"
                />
              </ExportFilenameDialog>
            )}
          </div>
        </DialogHeader>

        <div className="flex-grow rounded-md border bg-card">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
